import { observable, action, makeObservable } from "mobx";
import { today, isToday } from "@internationalized/date";
import config from "../../config";
import { DateTime } from "luxon";
import { toCalendarDate } from "shared/dateTimeUtils";

export default class TrendStore {
  // TODO: convert activeTrends to Set()
  activeTrends = {};
  trends = {};
  loadingTrends = {};
  date = today(config.timezone);
  constructor({ fetchTrends, appStore }) {
    makeObservable(this, {
      activeTrends: observable,
      trends: observable,
      loadingTrends: observable,
      date: observable,
      toggleTrendChart: action,
      fetchTrends: action,
      step: action,
    });

    this._fetchTrends = fetchTrends;
    this.toggleTrendChart = this.toggleTrendChart.bind(this);
    this.appStore = appStore;
  }

  get backDisabled() {
    return this.date.compare(this.minDay) <= 0;
  }
  get forwardDisabled() {
    return this.date.compare(today(config.timezone)) >= 0;
  }
  get minDay() {
    const hours =
      this.appStore.prefs?.userPreferences?.server?.pm?.dataRangeInHours || 0;
    return today().subtract({ days: Math.floor(hours / 24) });
  }
  get maxDay() {
    return today();
  }
  get isToday() {
    return isToday(this.date);
  }
  get dataRangeInHours() {
    return this.appStore.prefs?.userPreferences?.server?.pm?.dataRangeInHours;
  }

  step = (days) => {
    this.date = this.date.add({ days });
    this.fetchAllTrends();
  };

  discreteKey(discrete) {
    return discrete.key || discrete.keys.join();
  }
  isDiscreteActive(discrete) {
    return this.activeTrends[this.discreteKey(discrete)];
  }
  isTrendLoading(discrete) {
    return this.loadingTrends[this.discreteKey(discrete)];
  }

  get hasActiveTrends() {
    return !!Object.values(this.activeTrends).find(Boolean);
  }
  toggleTrendChart(discrete, chartEndTime) {
    if (!this.hasActiveTrends) {
      const _24HoursAgo = DateTime.now().minus({ hours: 24 });
      if (chartEndTime < _24HoursAgo) {
        this.date = toCalendarDate(chartEndTime);
      } else {
        this.date = today(config.timezone);
      }
    }
    const key = this.discreteKey(discrete);
    this.activeTrends[key] = !this.activeTrends[key];
    this.activeTrends = { ...this.activeTrends };
    if (this.activeTrends[key]) {
      const starttime = isToday(this.date) ? undefined : this.date;
      this.fetchTrends({ key, starttime });
    }
  }

  getActiveTrendCharts(discretes = []) {
    const activeTrendCharts = discretes
      .filter((discrete) => this.activeTrends[this.discreteKey(discrete)])
      .map((discrete) => {
        return {
          ...discrete,
          trend: this.trends[discrete.keys.join()],
        };
      });

    return activeTrendCharts;
  }

  fetchTrends({ key, starttime = this.date }) {
    if (this.loadingTrends[key]) {
      this.loadingTrends[key].abort();
    }
    const controller = new AbortController();
    this.loadingTrends[key] = controller;
    const { year, month, day } = starttime;

    return this._fetchTrends(
      {
        key,
        starttime:
          starttime &&
          (isToday(starttime)
            ? undefined
            : DateTime.local(year, month, day)
                .toUTC()
                .toISO({ suppressMilliseconds: true })),
      },
      { controller },
    )
      .then((trends) => {
        this.trends[key] = trends;
        this.trends = { ...this.trends };
      })
      .finally(() => {
        this.loadingTrends = {
          ...this.loadingTrends,
          [key]: false,
        };
      });
  }
  fetchAllTrends() {
    const compondKeys = Object.keys(this.activeTrends);
    const controller = new AbortController();
    compondKeys.forEach((key) => {
      if (this.loadingTrends[key]) {
        this.loadingTrends[key].abort();
      }
      this.loadingTrends[key] = controller;
    });
    const { year, month, day } = this.date;
    this.trends = {};
    return this._fetchTrends(
      {
        key: compondKeys.join(),
        starttime: isToday(this.date)
          ? undefined
          : DateTime.local(year, month, day, 0, 0, 0)
              .toUTC()
              .toISO({ suppressMilliseconds: true }),
      },
      { controller },
    )
      .then((trends) => {
        this.trends = Object.fromEntries(
          compondKeys.map((compoundKey) => {
            const keys = compoundKey.split(",");
            return [
              compoundKey,
              trends.filter((trend) => keys.includes(trend.key)),
            ];
          }),
        );
      })
      .finally(() => {
        this.loadingTrends = {
          ...this.loadingTrends,
          ...Object.fromEntries(
            compondKeys.map((compondKey) => [compondKey, false]),
          ),
        };
      });
  }

  setStartTime(day) {
    if (day.compare(this.minDay) < 0 || day.compare(this.maxDay) > 0) return;
    this.date = day;
    this.fetchAllTrends();
  }
}
